/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : DynamicFormTemplate.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
/**
 * @author : Narender - narender@au79consulting.com
 * @Date : 20-07-2024
 * @description : Custom dynamic form template component.
 */
import {
  Box,
  Divider,
  IconButton,
  Input,
  Paper,
  TextField,
  Typography,
} from "@mui/material";

import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";

import UpdateIcon from "@mui/icons-material/Update";
import CheckIcon from "@mui/icons-material/Check";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import appTheme from "../../assets/AppTheme/appTheme";
import CustomTextFieldN from "../text-fields/CustomTextFieldN";
import CustomButton from "../buttons/CustomButton";
import customConsole from "../../config/customConsole";
import { EDIT_TYPES } from "../../config/appConstants";
import CustomFormTextFieldV2 from "../customComponentsV2/common/textField/CustomFormTextFieldV2";
import { fontSizeDetails } from "../../config/appTheme";

const DynamicFormTemplate = ({
  showOnlyTemplate = false,
  showFieldsUpdate = false,
  handleEditUpdateField = () => { },
  handleEditField,
  handleDeleteField,
  headerOptionsList,
  signatureOptionsList,
  footerOptionsList,
  selectedHeaders,
  selectedSignatures,
  selectedFooters,
  dynamicFormData,
  setDynamicFormData,
}) => {
  /* Functions */

  /* useEffects */
  useEffect(() => {
    customConsole({ dynamicFormData });
  }, [dynamicFormData]);

  // sonar helper functions

  const updateHeaderTitle = (header, position, value) => {
    return header?.position === position ? { ...header, title: value } : header;
  };
  const headerTitleFunction = (headers, item, value) => {
    const updatedHeaders = headers.map((header) =>
      updateHeaderTitle(header, item?.position, value),
    );

    const isExists = headers.some(
      (header) => header?.position === item?.position,
    );

    const newHeader = !isExists
      ? [
        {
          image: "",
          position: item?.position,
          title: value,
          sub_title: "",
        },
      ]
      : [];

    return { updatedHeaders, newHeader };
  };

  // subtitle
  const updateHeaderSubtitle = (prev, value, item) => {
    const headers = prev?.headers || [];

    const exists = headers.some(
      (header) => header?.position === item?.position,
    );

    const updatedHeaders = headers.map((header) => {
      if (header?.position === item?.position) {
        return { ...header, sub_title: value };
      }
      return header;
    });

    if (!exists) {
      updatedHeaders.push({
        image: "",
        position: item?.position,
        title: "",
        sub_title: value,
      });
    }

    return {
      ...prev,
      headers: updatedHeaders,
    };
  };

  // field
  const updateFields = (prev, value, field) => {
    const updatedFields = (prev?.fields || []).map((field1) => {
      if (field1?.sequence === field?.sequence) {
        return { ...field1, value };
      }
      return field1;
    });

    return {
      ...prev,
      fields: updatedFields,
    };
  };

  // signature title
  const updateSignatureTitle = (prev, value, item) => {
    const signatures = prev?.signatures || [];

    const exists = signatures.some(
      (signature) => signature?.position === item?.position,
    );

    const updatedSignatures = signatures.map((signature) => {
      if (signature?.position === item?.position) {
        return { ...signature, title: value };
      }
      return signature;
    });

    if (!exists) {
      updatedSignatures.push({
        image: "",
        position: item?.position,
        title: value,
        sub_title: "",
      });
    }

    return {
      ...prev,
      signatures: updatedSignatures,
    };
  };

  //Signatire subtitle
  const updateSignatureSubTitle = (prev, value, item) => {
    const signatures = prev?.signatures || [];

    const exists = signatures.some(
      (signature) => signature?.position === item?.position,
    );

    const updatedSignatures = signatures.map((signature) => {
      if (signature?.position === item?.position) {
        return { ...signature, sub_title: value };
      }
      return signature;
    });

    if (!exists) {
      updatedSignatures.push({
        image: "",
        position: item?.position,
        title: "",
        sub_title: value,
      });
    }

    return {
      ...prev,
      signatures: updatedSignatures,
    };
  };

  // Footer title
  const updateFooterTitle = (prev, value, item) => {
    const footers = prev?.footers || [];

    const exists = footers.some(
      (footer) => footer?.position === item?.position,
    );

    const updatedFooters = footers.map((footer) => {
      if (footer?.position === item?.position) {
        return { ...footer, title: value };
      }
      return footer;
    });

    if (!exists) {
      updatedFooters.push({
        image: "",
        position: item?.position,
        title: value,
        sub_title: "",
      });
    }

    return {
      ...prev,
      footers: updatedFooters,
    };
  };
  // Footer subtitle
  const updateFooterSubTitle = (prev, value, item) => {
    const footers = prev?.footers || [];

    const exists = footers.some(
      (footer) => footer?.position === item?.position,
    );

    const updatedFooters = footers.map((footer) => {
      if (footer?.position === item?.position) {
        return { ...footer, sub_title: value };
      }
      return footer;
    });

    if (!exists) {
      updatedFooters.push({
        image: "",
        position: item?.position,
        title: "",
        sub_title: value,
      });
    }

    return {
      ...prev,
      footers: updatedFooters,
    };
  };
  return (
    <Paper
      elevation={2}
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        border: "1px solid grey",
        borderRadius: 1,
      }}
    >
      {/* Template Headers */}
      <Box sx={{ p: 1.4, display: "flex", gap: 2 }}>
        {headerOptionsList?.map((item, index) => (
          //  Enter field name
          <Box
            key={index}
            sx={{
              visibility: selectedHeaders?.includes(item?.position)
                ? "visible"
                : "hidden",
            }}
          >
            {/* <Typography
              variant="subtitle1"
              color={"grey"}
              component="label"
              htmlFor="form-type"
            >
              {`${item?.label} Header`}
            </Typography> */}

            <CustomFormTextFieldV2
              label="Header Title"
              required
              fldType="text"
              fullWidth
              id="form-type"
              type="text"
              size="small"
              value={
                dynamicFormData?.headers?.find(
                  (el) => el?.position === item?.position,
                )?.title || ""
              }
              placeholder="Header Title*"
              // setValue={(value) =>
              //   setDynamicFormData((prev) => ({
              //     ...prev,
              //     headers: prev?.headers
              //       ?.map((header) =>
              //         header?.position === item?.position
              //           ? { ...header, title: value }
              //           : header,
              //       )
              //       ?.concat(
              //         !prev?.headers?.some(
              //           (header) => header?.position === item?.position,
              //         )
              //           ? [
              //               {
              //                 image: "",
              //                 position: item?.position,
              //                 title: value,
              //                 sub_title: "",
              //               },
              //             ]
              //           : [],
              //       ),
              //   }))
              // }
              setValue={(value) => {
                setDynamicFormData((prev) => {
                  const headers = prev?.headers || [];
                  const { updatedHeaders, newHeader } = headerTitleFunction(
                    headers,
                    item,
                    value,
                  );
                  return {
                    ...prev,
                    headers: [...updatedHeaders, ...newHeader],
                  };
                });
              }}
            />

            <CustomFormTextFieldV2
              fullWidth
              label="Header Subtitle"
              type="text"
              size="small"
              value={
                dynamicFormData?.headers?.find(
                  (el) => el?.position === item?.position,
                )?.sub_title || ""
              }
              placeholder="Header Subtitle*"
              // setValue={(value) =>
              //   setDynamicFormData((prev) => ({
              //     ...prev,
              //     headers: prev?.headers
              //       ?.map((header) =>
              //         header?.position === item?.position
              //           ? { ...header, sub_title: value }
              //           : header,
              //       )
              //       ?.concat(
              //         !prev?.headers?.some(
              //           (header) => header?.position === item?.position,
              //         )
              //           ? [
              //               {
              //                 image: "",
              //                 position: item?.position,
              //                 title: "",
              //                 sub_title: value,
              //               },
              //             ]
              //           : [],
              //       ),
              //   }))
              // }
              setValue={(value) =>
                setDynamicFormData((prev) =>
                  updateHeaderSubtitle(prev, value, item),
                )
              }
            />
          </Box>
        ))}
      </Box>
      <Divider sx={{ border: "1px solid lightGrey" }} />
      {/* Template Fields */}
      <Box sx={{ flexGrow: 1, minHeight: "150px", m: 2.5, mb: 3.4 }}>
        {dynamicFormData?.fields
          ?.slice() // Create a shallow copy to avoid mutating the original array
          ?.sort((a, b) => a.sequence - b.sequence) // Sort by sequence in ascending order
          ?.map((field, index) => (
            <Box
              key={field?.sequence}
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                mb: 1.4,
              }}
            >
              <Typography
                variant="subtitle1"
                color={
                  field?.edit_type === EDIT_TYPES?.DDH
                    ? "blue"
                    : field?.edit_type === EDIT_TYPES?.SNOW
                      ? "grey"
                      : "black"
                }
                sx={{ fontSize: fontSizeDetails.body1.fontSize }}
              >
                {`${field?.sequence}. ${field?.field_name}: `}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mt: -1,
                }}
              >
                <TextField
                  InputProps={{
                    readOnly: showOnlyTemplate ? true : false,
                    disableUnderline: showOnlyTemplate ? true : false,
                  }}
                  disabled={field?.edit_type === EDIT_TYPES?.SNOW}
                  fullWidth
                  size="large"
                  variant="standard"
                  type={field?.field_type || "string"}
                  value={field?.value}
                  // onChange={(e) =>
                  //   setDynamicFormData((prev) => ({
                  //     ...prev,
                  //     fields: prev?.fields?.map((field1) =>
                  //       field1?.sequence === field?.sequence
                  //         ? { ...field1, value: e?.target?.value }
                  //         : field1,
                  //     ),
                  //   }))
                  // }
                  onChange={(e) =>
                    setDynamicFormData((prev) =>
                      updateFields(prev, e?.target?.value, field),
                    )
                  }
                  sx={{ ml: 1 }}
                />
                {showFieldsUpdate &&
                  field?.value?.length < 1 &&
                  field?.edit_type !== EDIT_TYPES?.SNOW && (
                    <>
                      <IconButton
                        size="small"
                        onClick={() => handleEditUpdateField(field?.field_name)}
                      >
                        <EditIcon
                          sx={{
                            color:
                              field?.edit_type === EDIT_TYPES?.DDH
                                ? "blue"
                                : field?.edit_type === EDIT_TYPES?.SNOW
                                  ? "grey"
                                  : "black",
                          }}
                        />
                      </IconButton>
                    </>
                  )}
                {!showOnlyTemplate && (
                  <>
                    <IconButton
                      size="small"
                      onClick={() => handleEditField(field?.sequence)}
                    >
                      <EditIcon
                        sx={{
                          fontSize: 16,
                          color:
                            field?.edit_type === EDIT_TYPES?.DDH
                              ? "blue"
                              : field?.edit_type === EDIT_TYPES?.SNOW
                                ? "grey"
                                : "black",
                        }}
                      />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteField(field?.sequence)}
                    >
                      <DeleteIcon sx={{ color: "black", fontSize: 16 }} />
                    </IconButton>
                  </>
                )}
              </Box>
            </Box>
          ))}
      </Box>
      {/* Template Signature */}
      <Box
        sx={{
          p: 1.4,
          display: "flex",
          justifyContent: "space-between",
          mb: 2,
          gap: 4,
        }}
      >
        {signatureOptionsList?.map((item, index) => (
          //  Enter field name
          <Box
            key={index}
            sx={{
              visibility: selectedSignatures?.includes(item?.position)
                ? "visible"
                : "hidden",
            }}
          >
            {/* <Typography
              variant="subtitle1"
              color={"grey"}
              component="label"
              htmlFor="form-type"
            >
              {`${item?.label} Footer`}
            </Typography> */}
            <CustomFormTextFieldV2
              label="Signature Title"
              fullWidth
              fldType="text"
              size="small"
              value={
                dynamicFormData?.signatures?.find(
                  (el) => el?.position === item?.position,
                )?.title || ""
              }
              placeholder="Signature Title*"
              disabled={showOnlyTemplate}
              // setValue={(value) =>
              //   setDynamicFormData((prev) => ({
              //     ...prev,
              //     signatures: prev?.signatures
              //       ?.map((signature) =>
              //         signature?.position === item?.position
              //           ? { ...signature, title: value }
              //           : signature,
              //       )
              //       ?.concat(
              //         !prev?.signatures?.some(
              //           (signature) => signature?.position === item?.position,
              //         )
              //           ? [
              //               {
              //                 image: "",
              //                 position: item?.position,
              //                 title: value,
              //                 sub_title: "",
              //               },
              //             ]
              //           : [],
              //       ),
              //   }))
              // }
              setValue={(value) =>
                setDynamicFormData((prev) =>
                  updateSignatureTitle(prev, value, item),
                )
              }
            />

            <CustomFormTextFieldV2
              label="Signature Subtitle"
              fullWidth
              fldType="text"
              size="small"
              value={
                dynamicFormData?.signatures?.find(
                  (el) => el?.position === item?.position,
                )?.sub_title || ""
              }
              placeholder="Signature Subtitle*"
              disabled={showOnlyTemplate}
              sx={{ mt: 2, mb: 0.4 }}
              // setValue={(value) =>
              //   setDynamicFormData((prev) => ({
              //     ...prev,
              //     signatures: prev?.signatures
              //       ?.map((signature) =>
              //         signature?.position === item?.position
              //           ? { ...signature, sub_title: value }
              //           : signature,
              //       )
              //       ?.concat(
              //         !prev?.signatures?.some(
              //           (signature) => signature?.position === item?.position,
              //         )
              //           ? [
              //               {
              //                 image: "",
              //                 position: item?.position,
              //                 title: "",
              //                 sub_title: value,
              //               },
              //             ]
              //           : [],
              //       ),
              //   }))
              // }
              setValue={(value) =>
                setDynamicFormData((prev) =>
                  updateSignatureSubTitle(prev, value, item),
                )
              }
            />
          </Box>
        ))}
      </Box>
      <Divider sx={{ border: "1px solid lightGrey" }} />
      {/* Template Footers */}
      <Box
        sx={{
          p: 1.4,
          display: "flex",
          gap: 2,
          justifyContent: "center",
          mt: "auto",
        }}
      >
        {footerOptionsList?.map((item, index) => (
          //  Enter field name
          <Box
            key={index}
            sx={{
              visibility: selectedFooters?.includes(item?.position)
                ? "visible"
                : "hidden",
            }}
          >
            {/* <Typography
              variant="subtitle1"
              color={"grey"}
              component="label"
              htmlFor="form-type"
            >
              {`${item?.label} Footer`}
            </Typography> */}
            <CustomFormTextFieldV2
              label="Footer Title"
              fullWidth
              fldType="text"
              size="small"
              value={
                dynamicFormData?.footers?.find(
                  (el) => el?.position === item?.position,
                )?.title || ""
              }
              placeholder="Footer Title*"
              disabled={showOnlyTemplate}
              // setValue={(value) =>
              //   setDynamicFormData((prev) => ({
              //     ...prev,
              //     footers: prev?.footers
              //       ?.map((footer) =>
              //         footer?.position === item?.position
              //           ? { ...footer, title: value }
              //           : footer,
              //       )
              //       ?.concat(
              //         !prev?.footers?.some(
              //           (footer) => footer?.position === item?.position,
              //         )
              //           ? [
              //               {
              //                 image: "",
              //                 position: item?.position,
              //                 title: value,
              //                 sub_title: "",
              //               },
              //             ]
              //           : [],
              //       ),
              //   }))
              // }
              setValue={(value) =>
                setDynamicFormData((prev) =>
                  updateFooterTitle(prev, value, item),
                )
              }
            />

            <CustomFormTextFieldV2
              label="Footer Subtitle"
              fullWidth
              fldType="text"
              size="small"
              value={
                dynamicFormData?.footers?.find(
                  (el) => el?.position === item?.position,
                )?.sub_title || ""
              }
              placeholder="Footer Subtitle*"
              disabled={showOnlyTemplate}
              sx={{ mt: 2, mb: 0.4 }}
              // setValue={(value) =>
              //   setDynamicFormData((prev) => ({
              //     ...prev,
              //     footers: prev?.footers
              //       ?.map((footer) =>
              //         footer?.position === item?.position
              //           ? { ...footer, sub_title: value }
              //           : footer,
              //       )
              //       ?.concat(
              //         !prev?.footers?.some(
              //           (footer) => footer?.position === item?.position,
              //         )
              //           ? [
              //               {
              //                 image: "",
              //                 position: item?.position,
              //                 title: "",
              //                 sub_title: value,
              //               },
              //             ]
              //           : [],
              //       ),
              //   }))
              // }
              setValue={(value) =>
                setDynamicFormData((prev) =>
                  updateFooterSubTitle(prev, value, item),
                )
              }
            />
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

export default DynamicFormTemplate;
